import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalesService } from '@/services/sales.service';
import { Account, AccountStatus } from '@/models/account.model';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FormInput, FormSelect, FormTextarea, LoadingSpinner, Card } from '@/components/common';
import { Building2, Globe, Phone, MapPin, FileText, ArrowLeft, Save } from 'lucide-react';

const accountStatuses: AccountStatus[] = ['prospect', 'customer', 'partner'];
const industries = [
  'Healthcare',
  'Technology',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Non-profit',
  'Other',
];

export const AccountForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Account>>({
    companyName: '',
    industry: '',
    website: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
    },
    status: 'prospect',
    assignedTo: profile?.id || '',
    contacts: [],
    notes: '',
  });

  const { data: account, isLoading } = useQuery({
    queryKey: ['account', id],
    queryFn: () => id ? SalesService.getAccount(id) : null,
    enabled: isEditing && !!id,
  });

  React.useEffect(() => {
    if (account) {
      setFormData(account);
    }
  }, [account]);

  const mutation = useMutation({
    mutationFn: async (data: Omit<Account, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (isEditing && id) {
        await SalesService.updateAccount(id, data);
      } else {
        await SalesService.createAccount(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      navigate('/sales');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync(formData as Omit<Account, 'id' | 'createdAt' | 'updatedAt'>);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner text="Loading account..." />
      </div>
    );
  }

  const accountStatusOptions = accountStatuses.map(status => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }));

  const industryOptions = [
    { value: '', label: 'Select industry' },
    ...industries.map(industry => ({
      value: industry,
      label: industry,
    })),
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/sales')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-8 h-8 text-blue-600" />
            {isEditing ? 'Edit Account' : 'New Account'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update account information' : 'Create a new company account'}
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <FormInput
                    label="Company Name"
                    icon={Building2}
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="Enter company name"
                  />
                </div>

                <FormSelect
                  label="Industry"
                  icon={Building2}
                  options={industryOptions}
                  value={formData.industry || ''}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                />

                <FormSelect
                  label="Status"
                  icon={Building2}
                  options={accountStatusOptions}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as AccountStatus })}
                />

                <FormInput
                  label="Website"
                  type="url"
                  icon={Globe}
                  value={formData.website || ''}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />

                <FormInput
                  label="Phone"
                  type="tel"
                  icon={Phone}
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <FormInput
                    label="Street"
                    icon={MapPin}
                    value={formData.address?.street || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value } as any,
                    })}
                    placeholder="123 Main Street"
                  />
                </div>

                <FormInput
                  label="City"
                  icon={MapPin}
                  value={formData.address?.city || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value } as any,
                  })}
                  placeholder="City"
                />

                <FormInput
                  label="State"
                  icon={MapPin}
                  value={formData.address?.state || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value } as any,
                  })}
                  placeholder="State"
                />

                <FormInput
                  label="Zip Code"
                  icon={MapPin}
                  value={formData.address?.zipCode || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, zipCode: e.target.value } as any,
                  })}
                  placeholder="12345"
                />

                <FormInput
                  label="Country"
                  icon={MapPin}
                  value={formData.address?.country || 'USA'}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value } as any,
                  })}
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <FormTextarea
                label="Notes"
                icon={FileText}
                rows={4}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any additional notes about this account..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/sales')}
              icon={ArrowLeft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              isLoading={mutation.isPending}
              iconPosition="right"
            >
              {isEditing ? 'Update Account' : 'Create Account'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

